import React, { useState } from 'react';
import { AvatarGroup, Avatar, Box } from '@mui/material';
import UserNavbarPopover from 'components/Core/UserNavbarPopover';
import UserOnlineStatus from 'components/Modals/UserOnlineChecker';
import { getUserStatusPriority } from '../Custom/userStatusHelper';
import { DateTime } from 'luxon';
import "../../css/components/Core/CommonAvatarGroup.css";
import { useTranslation } from "react-i18next";
import {useCachedImage} from "../../cache/useCachedImage";


const CachedAvatar = ({ src, alt, userId, ...props }) => {
  const cachedUrl = useCachedImage(src, null, `worker-${userId}`);
  return <Avatar alt={alt} src={cachedUrl} {...props} />;
};

const CommonAvatarGroup = ({ users, handleAvatarGroupClick, myLoggedUser }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleAvatarClick = (event, user) => {
    if (user) {
      setAnchorEl(event.currentTarget);
      setSelectedUser(user);
    }
  };

  const handleMoreClick = (event) => {
    event.stopPropagation();
    handleAvatarGroupClick(event);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  // Use the logged-in user's timezone for all comparisons
  const now = DateTime.now().setZone(myLoggedUser?.timezone);

  // Calculate and store statusPriority and message for each user
  const usersWithStatus = users.map((user) => {
    const { status, message } = getUserStatusPriority(user, now, t);
    return { ...user, statusPriority: status, message };
  });

  // Separate users into online and offline based on their statusPriority
  const onlineUsers = usersWithStatus.filter(user => user.statusPriority !== 4 && user.statusPriority !== 6);
  const offlineUsers = usersWithStatus.filter(user => user.statusPriority === 4 || user.statusPriority === 6);

  // Sort offline users by most recent online time, handling timezones with Luxon
  const sortedOfflineUsers = offlineUsers.sort((a, b) => {
    // Parse last_online time for both users with Luxon and their respective timezones
    const parseTimeWithTimezone = (user) => {
      return DateTime.fromISO(user.last_online, { zone: user.timezone });
    };

    const lastOnlineA = parseTimeWithTimezone(a);
    const lastOnlineB = parseTimeWithTimezone(b);

    // Calculate time since last seen for each
    const timeSinceLastSeenA = now.diff(lastOnlineA).milliseconds;
    const timeSinceLastSeenB = now.diff(lastOnlineB).milliseconds;

    return timeSinceLastSeenA - timeSinceLastSeenB;
  });

  // Combine online and sorted offline users, and sort the entire array by status priority
  const sortedUsers = [...onlineUsers, ...sortedOfflineUsers].sort((a, b) => a.statusPriority - b.statusPriority);

  return (
    <Box className="common-avatar-hide">
      <AvatarGroup max={5} className="common-avatar-group">
        {sortedUsers.slice(0, 4).map((user) => (
          <div key={user.id} className="common-avatar-users">
            <UserOnlineStatus
              userStatusAvailability={user.current_availability}
              statusPriority={user.statusPriority}
              message={user.message}
              showBadgeOnly={true}
            >
              <CachedAvatar
                  alt={user.full_name}
                  src={user.image}
                  userId={user.id}
                  onClick={(event) => handleAvatarClick(event, user)}
              />
            </UserOnlineStatus>
          </div>
        ))}
        {sortedUsers.length > 4 && (
          <div className="common-avatar-users">
            <Avatar onClick={handleMoreClick} className="common-avatar-group">
              +{users.length - 4}
            </Avatar>
          </div>
        )}
      </AvatarGroup>
      {selectedUser && (
        <UserNavbarPopover
          anchorEl={anchorEl}
          handleClose={handleClosePopover}
          user={selectedUser}
          myLoggedUser={myLoggedUser}
        />
      )}
    </Box>
  );
};

export default CommonAvatarGroup;
